import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { RecordResult } from 'src/app/models/record-result';
import { Record } from 'src/app/models/record';
import { Filter } from 'src/app/models/filter';
import { Search, SearchOrder, ViewMode } from 'src/app/state/user/searches/models/search';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { Subject, Subscription, distinctUntilChanged, takeUntil } from 'rxjs';
import { SearchModel } from 'src/app/models/search-model';
import { FilterValue } from 'src/app/models/filter-value';
import { LocaleService } from 'src/app/services/locale.service';
import { Rating } from 'src/app/models/rating';
import { ConfigService } from 'src/app/services/config.service';
import { PurchaseSuggestionsDialogComponent } from '../purchase-suggestions-dialog/purchase-suggestions-dialog.component';
import { Util } from 'src/app/util';
import { StorageService } from '../../services/storage.service';
import { ThemaService } from '../../services/thema.service';
import { YoutubeResultsDialogComponent } from '../youtube-results/youtube-results-dialog.component';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SEOService } from '../../services/seo.service';
import { SearchesFacade } from 'src/app/state/user/searches/searches.facade';
import { ToptenCollectionType } from 'src/app/models/collection';
import { RecordRequest } from 'src/app/models/record-request';
import { convertToBoolean } from 'src/app/_core/utils/convert-to-boolean';
import { ToastService } from '../../_core/services/toast.service';
import { AnalyticsService } from '../../_core/analytics/analytics.service';

const MAX_SEARCH_HISTORY_ITEMS = 20;

@Component({
  selector: 'app-results-component',
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss'],
})
export class ResultsComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly toastService = inject(ToastService);

  length: number;
  pageSize: number;
  pageSizeOptions: number[];
  filters: Filter[] = [];
  formatFilter: Filter;
  listFilter: Filter;
  records: Record[];
  recordResult: RecordResult;
  ratings: Rating[] = [];
  authService: AuthService;
  searchModelSub = new Subscription();
  selectedViewMode: ViewMode = 'GRID';
  searchModel: SearchModel = new SearchModel();
  searching: boolean;
  showPurchaseSuggestions: boolean;
  showExternalResults: boolean;
  showYoutubeButton: boolean;
  showViewToggleButton = true;
  sectionTitle: string;
  showReturnButton: boolean;
  originPage: string[];

  showFilters = true;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('accessibilityAutofocus', { static: true }) accessibilityAutofocus: ElementRef;

  tabletResize = '(max-width: 767px)';
  bpObserver = new Subject<void>();

  constructor(
    authService: AuthService,
    private configService: ConfigService,
    private apiService: ApiService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private localeService: LocaleService,
    private storageService: StorageService,
    private themaService: ThemaService,
    private broadcastService: BroadcastService,
    private breakpointObserver: BreakpointObserver,
    private readonly seoService: SEOService,
    private searchesFacade: SearchesFacade
  ) {
    this.authService = authService;
    this.pageSize = this.searchModel.limit;
    this.pageSizeOptions = [24, 48];

    this.searchModelSub = broadcastService.searchModel$.subscribe((model) => (this.searchModel = model));
    this.showPurchaseSuggestions = this.shouldShowPurchaseSuggestions();
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.sectionTitle = params['title'] == null ? 'results.title' : params['title'];
      if (params['showReturnButton']) {
        this.showReturnButton = convertToBoolean(params['showReturnButton']);
      } else {
        this.showReturnButton = true;
      }

      if (params['originPage']) {
        this.originPage = [params['originPage']];
      } else {
        this.originPage = [];
      }

      const searchModel = new SearchModel(params, this.configService.get().resultsFilterOrder);
      this.themaService.fillTermLabels(searchModel.terms);
      const pageIndex = searchModel.offset / searchModel.limit;

      this.pageSize = searchModel.limit;
      if (this.paginator) {
        this.paginator.pageIndex = pageIndex;
      }

      if (searchModel.terms !== this.searchModel.terms) {
        this.showPurchaseSuggestions = this.shouldShowPurchaseSuggestions();
      }

      this.searchModel = searchModel;
      this.showFilters = params.topten !== ToptenCollectionType.GENERIC_BY_SELECTION;

      this.broadcastService.broadcastSearchModel(searchModel);
      this.getRecords(params);
    });

    this.paginator?.page.subscribe((event: PageEvent) => {
      this.records = [];
      this.searchModel.limit = event.pageSize;
      this.searchModel.offset = event.pageIndex * event.pageSize;

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          limit: this.searchModel.limit,
          offset: this.searchModel.offset,
        },
        queryParamsHandling: 'merge',
      });
    });

    this.seoService.createCanonicalLink(window.location.origin);

    this.breakpointObserver
      .observe(this.tabletResize)
      .pipe(takeUntil(this.bpObserver), distinctUntilChanged())
      .subscribe((resizeMathed) => {
        const selectedViewModeCached = this.storageService.get('viewMode') as ViewMode;
        const resultsViewMode = this.configService.get().resultsViewMode;

        if (this.configService.isParana() || this.configService.isLeiasp()) {
          this.selectedViewMode = 'GRID';
          this.showViewToggleButton = false;
          return;
        }

        if (resizeMathed.matches) {
          this.selectedViewMode = 'GRID';
          return;
        }

        if (resultsViewMode && !selectedViewModeCached) {
          this.selectedViewMode = resultsViewMode.toLocaleUpperCase() as ViewMode;
          return;
        }

        this.selectedViewMode = selectedViewModeCached ?? 'GRID';
      });
  }

  ngOnDestroy() {
    this.broadcastService.broadcastSearchModel(new SearchModel(undefined, this.configService.get().resultsFilterOrder));
    this.searchModelSub.unsubscribe();
    this.bpObserver.next();
    this.bpObserver.complete();
  }

  ngAfterViewInit() {
    this.accessibilityAutofocus?.nativeElement.focus();
  }

  ngAfterViewChecked() {
    const list = document.getElementsByClassName('mat-paginator-range-label');

    this.localeService.getString(['pagination.page', 'pagination.of']).subscribe((values) => {
      const page = values['pagination.page'];
      const of = values['pagination.of'];
      list[0].innerHTML = `${page} ${this.paginator.pageIndex + 1} ${of} ${this.paginator.getNumberOfPages()}`;
    });
  }

  goToRecord(record: Record, index: number) {
    if (record.wishListInfo !== undefined) {
      return this.router.navigate(['/list', Util.slugifyListRecord(record)], {
        state: this.getState(index),
      });
    }
    return this.router.navigate(['/info', Util.slugifyRecord(record)], {
      state: this.getState(index),
    });
  }

  getState(index: number): object {
    return {
      index: index,
      page: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      records: this.records.map((_record) => ({
        id: _record.id,
        title: _record.title,
        wishListInfo: _record.wishListInfo,
      })),
      total: this.length,
      genesis: this.searchModel,
      searchModel: this.searchModel,
      from: 'RESULT_SCREEN',
    };
  }

  private addToSearchHistory() {
    const request = this.searchModel.toRequest();

    if (!this.authService.isAuthenticated() || (!request.query && !request.facets)) {
      return;
    }

    const search: Search = {
      userId: this.userService.getUserId(),
      query: request.query || null,
      facets: request.facets || null,
      searchDate: new Date(),
      total: this.length,
      collectionId: this.searchModel.collectionId?.toString() || null,
      careerPlanLevelId: this.searchModel.careerPlanLevelId?.toString() || null,
      challengeId: this.searchModel.challengeId?.toString() || null,
    };

    if (!this.searchesFacade.searchHistory.length) {
      this.searchesFacade.getSearchHistory().subscribe(() => {
        this.checkSearchHistoryToSave(search);
      });
    } else {
      this.checkSearchHistoryToSave(search);
    }
  }

  private checkSearchHistoryToSave(search: Search) {
    if (this.searchesFacade.isSearchSaved(search, true)) {
      return;
    }
    this.checkSearchHistoryItems();
    this.searchesFacade.postSavedSearchToHistory(search).subscribe();
  }

  private checkSearchHistoryItems() {
    if (this.searchesFacade.searchHistory.length === MAX_SEARCH_HISTORY_ITEMS) {
      this.searchesFacade.searchHistory.pop();
    }
  }

  private getRecords(params?: Params) {
    let request: RecordRequest;
    if (convertToBoolean(params['excludeNonLeRecords'])) {
      request = this.searchModel.toRequestLearningExperiences();
    } else {
      request = this.searchModel.toRequest();
    }

    if (params?.order) {
      request.order = params.order;
    }

    if (params['careerPlanLevelId']) {
      request.careerPlanLevelId = params['careerPlanLevelId'];
    }

    if (params['challengeId']) {
      request.challengeId = params['challengeId'];
    }

    this.searching = true;

    const availability = !(this.configService.isParana() || this.configService.isLeiasp());
    const origin = this.analyticsService.searchPerformedOrigin;
    const appliedFacets = this.searchModel.filters.map((f) => f.name);

    this.apiService.getRecords(request, availability, true).subscribe({
      next: (result) => {
        this.recordResult = result;
        this.records = result.records;
        this.filters = result.facets || [];
        this.length = result.total;
        this.formatFilter = this.filters.find((f) => f.name === Util.FORMAT_FILTER);
        this.listFilter = this.filters.find((f) => f.name === 'listas_ls');

        this.broadcastService.broadcastFilters(this.filters);
        this.addToSearchHistory();
        this.getRatings();
        this.searching = false;

        this.analyticsService.sendSearchPerformedEvent(appliedFacets, result.records?.length > 0, origin);
      },
      error: () => {
        this.analyticsService.sendSearchPerformedEvent(appliedFacets, false, origin);
      },
    });

    this.configureDisplayExternalDbSection();
  }

  private shouldShowPurchaseSuggestions(): boolean {
    return this.configService.get().showPurchaseSuggestions && this.authService.isAuthenticated();
  }

  onFilterSelected(selectedFilter: FilterValue) {
    this.searchModel.applyFilter(selectedFilter);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        facets: this.searchModel.toRequest().facets,
      },
      queryParamsHandling: 'merge',
    });
  }

  saveSearch() {
    const request = this.searchModel.toRequest();

    const search: Search = {
      query: request.query || null,
      facets: request.facets || null,
      userId: this.userService.getUserId(),
      searchDate: new Date(),
      total: this.length,
      collectionId: this.searchModel.collectionId?.toString() || null,
      careerPlanLevelId: this.searchModel.careerPlanLevelId?.toString() || null,
      challengeId: this.searchModel.challengeId?.toString() || null,
    };

    if (!search.query && !search.facets && !search.collectionId && !search.careerPlanLevelId && !search.challengeId) {
      return this.toastService.showToast('messages.search.empty', 'info');
    }
    if (!this.searchesFacade.savedSearches.length) {
      this.searchesFacade.getSavedSearches().subscribe(() => {
        this.checkSavedSearchToSave(search);
      });
    } else {
      this.checkSavedSearchToSave(search);
    }
  }

  private checkSavedSearchToSave(search: Search) {
    if (this.searchesFacade.isSearchSaved(search)) {
      return this.toastService.showToast('messages.search.duped', 'info');
    }

    this.searchesFacade.postSearch(search).subscribe(() => {
      this.toastService.showToast('messages.search.saved');
    });
  }

  sortBy(sortMode: SearchOrder) {
    this.searchModel.order = sortMode;
    this.setOrder(`${sortMode.field}:${sortMode.order}`);
  }

  setOrder(order: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        order: order,
      },
      queryParamsHandling: 'merge',
    });
  }

  toggleViewMode() {
    if (this.selectedViewMode === 'GRID') {
      this.selectedViewMode = 'LIST';
    } else {
      this.selectedViewMode = 'GRID';
    }

    this.storageService.set('viewMode', this.selectedViewMode);
  }

  getRatings() {
    if (!this.recordResult.total) {
      return;
    }

    const byIsbn = this.records.filter((r) => r.isbn).map((r) => r.isbn);
    const byRecordId = this.records.filter((r) => !r.isbn).map((r) => r.id);

    let userId: string;

    if (this.authService.isAuthenticated()) {
      userId = this.userService.getUserId();
    }

    this.apiService.getRecordRatings(byRecordId, byIsbn, userId).subscribe((ratings) => (this.ratings = ratings));
  }

  getRatingByRecord(record: Record): Rating {
    return this.ratings.find((r) => [record.id, record.isbn].includes(r.id));
  }

  suggestPurchase() {
    const searchModel = new SearchModel();
    searchModel.terms = this.searchModel.terms;

    this.dialog.open(PurchaseSuggestionsDialogComponent, {
      data: searchModel,
    });
  }

  youtubeResults() {
    const searchModel = new SearchModel();
    searchModel.terms = [...this.searchModel.terms];

    this.dialog.open(YoutubeResultsDialogComponent, {
      data: searchModel,
    });
  }

  configureDisplayExternalDbSection() {
    this.showYoutubeButton =
      this.configService.get().youtubeIntegration && this.authService.isAuthenticated() && this.searchModel.hasTerms();
    this.showExternalResults = this.showYoutubeButton; // in the future, this will take into account buttons associated to other dbs
  }
}
